import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, apiError, eur, minimoMsg } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SLIDER_MIN, SLIDER_MAX, sliderMinFor } from "@/lib/constants";
import { SinistriDeclaration, SINISTRI_YEARS } from "@/components/SinistriDeclaration";
import { IddDeclaration } from "@/components/IddDeclaration";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Calculator, ShieldCheck, AlertTriangle, XCircle, Wallet, Save, Loader2, Lock, HeartHandshake } from "lucide-react";

const COVERAGE_LABELS = {
  rct: "RCT - Responsabilità Civile Terzi", rco: "RCO - Prestatori di Lavoro",
  organi_sociali: "RC Organi Sociali", tutela_legale: "Tutela Legale",
};
const UW_BOOLS = [
  ["contratto_annullato_sinistro", "Contratto precedente annullato a seguito di sinistro"],
  ["sinistri_in_corso", "Sostituzione di polizza con sinistri in corso"],
  ["premi_arretrati", "Presenza di premi arretrati su polizza in essere"],
  ["rischio_gia_coperto", "Rischio già coperto da altra compagnia"],
];

export default function QuoteEngine() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [anagrafiche, setAnagrafiche] = useState([]);
  const [anagraficaId, setAnagraficaId] = useState(params.get("anagrafica") || "");
  const requestId = params.get("request");
  const intakeId = params.get("intake");
  const editId = params.get("edit");
  const { isAdmin, user } = useAuth();
  const sliderMin = sliderMinFor(user?.role);
  const [prefilled, setPrefilled] = useState(false);
  const [editLoaded, setEditLoaded] = useState(false);

  const [sectionCode, setSectionCode] = useState("non_sanitaria");
  const [ambito, setAmbito] = useState("non_sanitaria");
  const [settorePrincipale, setSettorePrincipale] = useState("");
  const [tipoPrincipale, setTipoPrincipale] = useState("");
  const [parameterCode, setParameterCode] = useState("fatturato");
  const [parameterValue, setParameterValue] = useState("");
  const [massimale, setMassimale] = useState("");
  const [riskClass, setRiskClass] = useState("1");
  const [slider, setSlider] = useState(0);
  const [answers, setAnswers] = useState({ tipologia_ente: "", forma_giuridica: "", benestare_direzione: false });
  const [cov, setCov] = useState({
    rco: { selected: false, abrogazione: false, massimale: 0 },
    organi_sociali: { selected: false, massimale: 25000 },
    tutela_legale: { selected: false, option: "tutti_componenti", massimale: 15000, soci: 0 },
  });
  const [maggiorazioni, setMaggiorazioni] = useState([]);
  const [leadRequested, setLeadRequested] = useState([]);
  const [sinistri, setSinistri] = useState(null);
  const [idd, setIdd] = useState(null);
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/rc/config").then(({ data }) => setConfig(data));
    api.get("/anagrafiche").then(({ data }) => setAnagrafiche(data));
  }, []);

  const section = config?.sections?.[sectionCode];
  const paramMeta = section?.parameters?.[parameterCode];
  const tipoMeta = (config?.sanitary_tipi || []).find((t) => t.code === tipoPrincipale) || null;

  // Non sanitaria: Settore Principale guida il parametro tariffario (Assimoco)
  const nonsanSettori = config?.nonsan_settori || [];
  const sportTipi = config?.nonsan_sport_tipi || [];
  const genericTipi = config?.nonsan_generic_tipi || [];
  const settoreMeta = nonsanSettori.find((s) => s.label === settorePrincipale) || null;
  const settoreMode = settoreMeta?.param_mode || null; // choice | tesserati | anime

  // Ammissibilita' garanzie opzionali per Settore Principale (Norme Tariffarie)
  const covElig = config?.coverage_eligibility || {};
  const isCovEligible = (code) => {
    const rule = covElig[code];
    if (!rule) return true;
    if (rule.block_sanitaria && ambito === "sanitaria") return false;
    const s = settorePrincipale || "";
    if (!s) return false;
    if (rule.mode === "allow_list") return (rule.settori || []).includes(s);
    if (rule.mode === "block_list") return !(rule.settori || []).includes(s);
    return true;
  };
  const organiEligible = useMemo(() => isCovEligible("organi_sociali"), [config, settorePrincipale, ambito]);
  const tutelaEligible = useMemo(() => isCovEligible("tutela_legale"), [config, settorePrincipale, ambito]);

  // Deriva la sezione tariffaria dall'ambito e dal Tipo Principale (procedura Assimoco)
  useEffect(() => {
    if (!config) return;
    if (ambito === "non_sanitaria") {
      setSectionCode("non_sanitaria");
    } else if (ambito === "sanitaria") {
      if (tipoMeta) setSectionCode(tipoMeta.section);
      else setSectionCode((sc) => (sc && sc.startsWith("sanitaria") ? sc : "sanitaria_basso_rischio"));
    }
  }, [ambito, tipoPrincipale, config, tipoMeta]);

  // Non sanitaria: imposta il parametro in base al Settore Principale
  useEffect(() => {
    if (ambito !== "non_sanitaria" || !settoreMode) return;
    if (settoreMode === "tesserati") setParameterCode("tesserati");
    else if (settoreMode === "anime") setParameterCode("anime");
    else setParameterCode((pc) => (pc === "fatturato" || pc === "soci" ? pc : "fatturato"));
  }, [ambito, settoreMode]);

  // Enti sportivi: il Tipo Principale determina la classe di rischio
  useEffect(() => {
    if (ambito === "non_sanitaria" && settoreMode === "tesserati" && tipoPrincipale) {
      const t = sportTipi.find((x) => x.code === tipoPrincipale);
      if (t) setRiskClass(t.risk_class);
    }
  }, [tipoPrincipale, settoreMode, ambito]);

  // Deseleziona automaticamente le garanzie non ammesse per il soggetto scelto
  useEffect(() => {
    if (!organiEligible) setCov((c) => (c.organi_sociali.selected ? { ...c, organi_sociali: { ...c.organi_sociali, selected: false } } : c));
  }, [organiEligible]);
  useEffect(() => {
    if (!tutelaEligible) setCov((c) => (c.tutela_legale.selected ? { ...c, tutela_legale: { ...c.tutela_legale, selected: false } } : c));
  }, [tutelaEligible]);

  useEffect(() => {
    if (section) {
      const first = Object.keys(section.parameters)[0];
      setParameterCode((pc) => (section.parameters[pc] ? pc : first));
      setMassimale((m) => (section.massimali.includes(Number(m)) ? m : String(section.massimali[0])));
    }
  }, [sectionCode, section]);

  // Pre-compilazione automatica dai dati raccolti via link pubblico (intake)
  useEffect(() => {
    if (!config || !intakeId || prefilled) return;
    api.get(`/intakes/${intakeId}`).then(({ data }) => {
      const d = data.client_data || {};
      if (d.sezione_rischio && config.sections[d.sezione_rischio]) {
        setSectionCode(d.sezione_rischio);
        if (d.sezione_rischio.startsWith("sanitaria")) setAmbito("sanitaria");
        else setAmbito("non_sanitaria");
      }
      if (d.parametro) setParameterCode(d.parametro);
      if (d.parametro_valore) setParameterValue(String(d.parametro_valore));
      if (d.massimale) setMassimale(String(d.massimale));
      if (d.classe_rischio) setRiskClass(String(d.classe_rischio));
      // Settore / Tipo Principale (procedura Assimoco) dal link pubblico
      if (d.settore_principale) setSettorePrincipale(d.settore_principale);
      if (d.tipo_principale) {
        if (d.sezione_rischio && d.sezione_rischio.startsWith("sanitaria")) {
          const t = (config.sanitary_tipi || []).find((x) => x.label === d.tipo_principale);
          if (t) setTipoPrincipale(t.code);
        } else {
          const sm = (config.nonsan_settori || []).find((s) => s.label === d.settore_principale)?.param_mode;
          if (sm === "tesserati") {
            const t = (config.nonsan_sport_tipi || []).find((x) => x.label === d.tipo_principale);
            if (t) setTipoPrincipale(t.code);
          } else if (sm === "choice") {
            setTipoPrincipale(d.tipo_principale);
          }
        }
      }
      setAnswers((a) => ({ ...a, tipologia_ente: d.tipo_ente || a.tipologia_ente }));
      setCov((c) => ({
        rco: { ...c.rco, selected: !!d.vuole_rco },
        organi_sociali: { ...c.organi_sociali, selected: !!d.vuole_organi_sociali },
        tutela_legale: { ...c.tutela_legale, selected: !!d.vuole_tutela_legale },
      }));
      const req = [];
      if (d.vuole_organi_sociali) req.push("organi_sociali");
      if (d.vuole_tutela_legale) req.push("tutela_legale");
      setLeadRequested(req);
      if (d.vuole_direttore_sanitario) setMaggiorazioni((m) => (m.includes("direttore_sanitario") ? m : [...m, "direttore_sanitario"]));
      if (d.sinistri) setSinistri(d.sinistri);
      if (d.idd) setIdd(d.idd);
      setPrefilled(true);
      toast.success("Dati del cliente pre-compilati dal link");
    }).catch(() => setPrefilled(true));
  }, [config, intakeId, prefilled]);

  // Modalità modifica: carica un preventivo esistente e pre-compila tutti i campi
  useEffect(() => {
    if (!config || !editId || editLoaded) return;
    api.get(`/quotes/${editId}`).then(({ data: q }) => {
      setAnagraficaId(q.anagrafica_id || "");
      if (q.section_code) setSectionCode(q.section_code);
      if (q.parameter_code) setParameterCode(q.parameter_code);
      setParameterValue(String(q.parameter_value ?? ""));
      if (q.massimale) setMassimale(String(q.massimale));
      if (q.risk_class) setRiskClass(String(q.risk_class));
      setSlider(q.slider_pct || 0);
      const ua = q.underwriting_answers || { tipologia_ente: "", forma_giuridica: "", benestare_direzione: false };
      setAnswers(ua);
      if (q.section_code && q.section_code.startsWith("sanitaria")) {
        setAmbito("sanitaria");
        if (ua.settore_principale) setSettorePrincipale(ua.settore_principale);
        if (ua.tipo_principale) {
          const t = (config.sanitary_tipi || []).find((x) => x.label === ua.tipo_principale);
          if (t) setTipoPrincipale(t.code);
        }
      } else if (q.section_code === "non_sanitaria" && ua.settore_principale) {
        setAmbito("non_sanitaria");
        setSettorePrincipale(ua.settore_principale);
        const sm = (config.nonsan_settori || []).find((s) => s.label === ua.settore_principale)?.param_mode;
        if (sm === "tesserati") {
          const t = (config.nonsan_sport_tipi || []).find((x) => x.label === ua.tipo_principale);
          if (t) setTipoPrincipale(t.code);
        } else if (sm === "choice") {
          setTipoPrincipale(ua.tipo_principale || "");
        }
      }
      setMaggiorazioni(q.maggiorazioni || []);
      if (q.sinistri) setSinistri(q.sinistri);
      if (q.idd) setIdd(q.idd);
      const oc = q.optional_coverages || [];
      const find = (c) => oc.find((o) => o.coverage_code === c);
      setCov({
        rco: { selected: !!find("rco"), abrogazione: false, massimale: q.rco_massimale || 0 },
        organi_sociali: { selected: !!find("organi_sociali"), massimale: find("organi_sociali")?.massimale || 25000 },
        tutela_legale: { selected: !!find("tutela_legale"), option: find("tutela_legale")?.option || "tutti_componenti", massimale: find("tutela_legale")?.massimale || 15000, soci: find("tutela_legale")?.soci || 0 },
      });
      setEditLoaded(true);
      setLeadRequested(q.requested_coverages || []);
      toast.info("Modalità modifica: aggiorna i dati e salva per ricalcolare");
    }).catch(() => setEditLoaded(true));
  }, [config, editId, editLoaded]);

  const buildPayload = () => {
    const optional_coverages = [];
    if (cov.rco.selected) optional_coverages.push({ coverage_code: "rco", selected: true, abrogazione_franchigia: cov.rco.abrogazione });
    if (cov.organi_sociali.selected) optional_coverages.push({ coverage_code: "organi_sociali", selected: true, massimale: cov.organi_sociali.massimale });
    if (cov.tutela_legale.selected) optional_coverages.push({ coverage_code: "tutela_legale", selected: true, option: cov.tutela_legale.option, massimale: cov.tutela_legale.massimale, soci: Number(cov.tutela_legale.soci) });
    const uw = { ...answers };
    if (ambito === "sanitaria") {
      uw.settore_principale = settorePrincipale;
      uw.tipo_principale = tipoMeta?.label || "";
    } else if (ambito === "non_sanitaria" && settorePrincipale) {
      uw.settore_principale = settorePrincipale;
      if (settoreMode === "tesserati") uw.tipo_principale = sportTipi.find((x) => x.code === tipoPrincipale)?.label || "";
      else if (settoreMode === "anime") uw.tipo_principale = config?.nonsan_parrocchie_tipo || "";
      else uw.tipo_principale = tipoPrincipale || "";
    }
    return {
      section_code: sectionCode, parameter_code: parameterCode, parameter_value: Number(parameterValue || 0),
      massimale: Number(massimale), rco_massimale: cov.rco.selected && cov.rco.massimale ? Number(cov.rco.massimale) : Number(massimale),
      risk_class: riskClass, slider_pct: slider,
      underwriting_answers: uw, optional_coverages, maggiorazioni, sinistri, idd,
      requested_coverages: leadRequested,
    };
  };

  const calculate = async () => {
    setCalculating(true);
    try {
      const { data } = await api.post("/quotes/calculate", buildPayload());
      setResult(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCalculating(false);
    }
  };

  const save = async () => {
    if (!anagraficaId) return toast.error("Seleziona un'anagrafica");
    setSaving(true);
    try {
      const { data } = await api.post("/quotes", { ...buildPayload(), anagrafica_id: anagraficaId, request_id: requestId, quote_id: editId || undefined });
      toast.success(editId ? "Preventivo aggiornato" : "Preventivo salvato");
      navigate(isAdmin ? `/admin/preventivi?open=${data.id}` : `/preventivi/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleMagg = (code) => setMaggiorazioni((m) => (m.includes(code) ? m.filter((x) => x !== code) : [...m, code]));

  const statusBanner = useMemo(() => {
    if (!result) return null;
    if (result.status === "non_ammissibile")
      return { icon: XCircle, tone: "bg-red-50 border-red-200 text-red-800", title: "Rischio non assumibile",
        msgs: result.eligibility.blocking_errors };
    if (result.binario === "referral_tecnico")
      return { icon: AlertTriangle, tone: "bg-amber-50 border-amber-200 text-amber-800", title: "Referral tecnico richiesto",
        msgs: [...(result.eligibility.warnings || []), ...(result.authority_exceeded ? [`Sconto/aumento oltre la tua autonomia (max +${result.slider_max_autonomo}%).`] : []), "Emissione autonoma bloccata: invia all'Agenzia."] };
    return { icon: ShieldCheck, tone: "bg-emerald-50 border-emerald-200 text-emerald-800", title: "Preventivo standard — emettibile",
      msgs: [...(result.eligibility.warnings || []), ...minimoMsg(result)] };
  }, [result]);

  if (!config) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <div className={`mb-3 flex flex-wrap gap-2 ${editId ? "hidden" : ""}`} data-testid="product-switch">
          <span className="rounded-full border border-forest bg-forest px-4 py-1.5 text-sm font-medium text-white">
            RC Economia Solidale
          </span>
          <Link to={`/quota/mrv${[anagraficaId ? `anagrafica=${anagraficaId}` : "", requestId ? `request=${requestId}` : ""].filter(Boolean).length ? `?${[anagraficaId ? `anagrafica=${anagraficaId}` : "", requestId ? `request=${requestId}` : ""].filter(Boolean).join("&")}` : ""}`}
            data-testid="switch-to-mrv"
            className="flex items-center gap-1.5 rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">
            <HeartHandshake className="h-4 w-4" /> Tutela Multirischi Volontariato
          </Link>
          <Link to={`/quota/infortuni${[anagraficaId ? `anagrafica=${anagraficaId}` : "", requestId ? `request=${requestId}` : ""].filter(Boolean).length ? `?${[anagraficaId ? `anagrafica=${anagraficaId}` : "", requestId ? `request=${requestId}` : ""].filter(Boolean).join("&")}` : ""}`}
            data-testid="switch-to-inf"
            className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">
            Infortuni Economia Solidale
          </Link>
          <Link to={`/quota/catnat${[anagraficaId ? `anagrafica=${anagraficaId}` : "", requestId ? `request=${requestId}` : ""].filter(Boolean).length ? `?${[anagraficaId ? `anagrafica=${anagraficaId}` : "", requestId ? `request=${requestId}` : ""].filter(Boolean).join("&")}` : ""}`}
            data-testid="switch-to-catnat"
            className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">
            CAT NAT Assimoco
          </Link>
          <Link to={`/quota/multirischi${[anagraficaId ? `anagrafica=${anagraficaId}` : "", requestId ? `request=${requestId}` : ""].filter(Boolean).length ? `?${[anagraficaId ? `anagrafica=${anagraficaId}` : "", requestId ? `request=${requestId}` : ""].filter(Boolean).join("&")}` : ""}`}
            data-testid="switch-to-multirischi"
            className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">
            Multirischi Danni ai Beni
          </Link>
        </div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Preventivo — RC Economia Solidale</h1>
        <p className="mt-1 text-muted-foreground">Ed. {config.product_version} · fedeltà 1:1 con il quotatore ufficiale.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <div className="space-y-5 lg:col-span-3">
          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold text-forest">Dati di rischio</h2>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Anagrafica cliente</Label>
                <Select value={anagraficaId} onValueChange={setAnagraficaId}>
                  <SelectTrigger data-testid="quote-anagrafica"><SelectValue placeholder="Seleziona ente..." /></SelectTrigger>
                  <SelectContent>{anagrafiche.map((a) => <SelectItem key={a.id} value={a.id}>{a.ragione_sociale}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Ambito di rischio</Label>
                <Select value={ambito} onValueChange={(v) => { setAmbito(v); if (v === "non_sanitaria") { setSettorePrincipale(""); setTipoPrincipale(""); } }}>
                  <SelectTrigger data-testid="quote-ambito"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="non_sanitaria">RC Non Sanitaria / Socio-sanitaria</SelectItem>
                    <SelectItem value="sanitaria">RC Rischio Sanitario</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {ambito === "sanitaria" && (
                <>
                  <div className="space-y-1.5">
                    <Label>Settore Principale <span className="text-terracotta">*</span></Label>
                    <Select value={settorePrincipale} onValueChange={setSettorePrincipale}>
                      <SelectTrigger data-testid="quote-settore-principale"><SelectValue placeholder="Seleziona tipologia cliente..." /></SelectTrigger>
                      <SelectContent>{(config.sanitary_settori || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Tipo Principale <span className="text-terracotta">*</span></Label>
                    <Select value={tipoPrincipale} onValueChange={setTipoPrincipale}>
                      <SelectTrigger data-testid="quote-tipo-principale"><SelectValue placeholder="Seleziona tipologia rischio..." /></SelectTrigger>
                      <SelectContent>{(config.sanitary_tipi || []).map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                    {tipoMeta && section && (
                      <p className="text-[11px] text-muted-foreground">Sezione tariffaria applicata: <span className="font-medium text-forest">{section.label}</span></p>
                    )}
                  </div>
                </>
              )}
              {ambito === "non_sanitaria" && (
                <>
                  <div className="space-y-1.5">
                    <Label>Settore Principale <span className="text-terracotta">*</span></Label>
                    <Select value={settorePrincipale} onValueChange={(v) => { setSettorePrincipale(v); setTipoPrincipale(""); }}>
                      <SelectTrigger data-testid="quote-settore-principale-ns"><SelectValue placeholder="Seleziona tipologia cliente..." /></SelectTrigger>
                      <SelectContent>{nonsanSettori.map((s) => <SelectItem key={s.label} value={s.label}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {settoreMode === "tesserati" && (
                    <div className="space-y-1.5">
                      <Label>Tipo Principale <span className="text-terracotta">*</span></Label>
                      <Select value={tipoPrincipale} onValueChange={setTipoPrincipale}>
                        <SelectTrigger data-testid="quote-tipo-principale-sport"><SelectValue placeholder="Seleziona attività sportiva..." /></SelectTrigger>
                        <SelectContent>{sportTipi.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                      {tipoPrincipale && (
                        <p className="text-[11px] text-muted-foreground">Parametro: n° tesserati sportivi · Classe di rischio automatica: <span className="font-medium text-forest">{riskClass === "3" ? "RD (riservato Direzione)" : `Classe ${riskClass}`}</span></p>
                      )}
                    </div>
                  )}
                  {settoreMode === "anime" && (
                    <div className="space-y-1.5">
                      <Label>Tipo Principale</Label>
                      <div data-testid="quote-tipo-principale-parrocchie" className="rounded-md border border-border/60 bg-sage/30 px-3 py-2 text-sm text-forest">{config.nonsan_parrocchie_tipo}</div>
                      <p className="text-[11px] text-muted-foreground">Parametro: n° anime / parrocchiani.</p>
                    </div>
                  )}
                  {settoreMode === "choice" && (
                    <div className="space-y-1.5">
                      <Label>Tipo Principale (attività)</Label>
                      <SearchableSelect testid="quote-tipo-principale-generic" options={genericTipi}
                        value={tipoPrincipale} onChange={setTipoPrincipale}
                        placeholder="Seleziona o cerca l'attività..." />
                      <p className="text-[11px] text-muted-foreground">Digita per cercare tra le attività assicurabili (procedura Assimoco).</p>
                    </div>
                  )}
                </>
              )}
              <div className="grid grid-cols-2 gap-4">
                {ambito === "non_sanitaria" && settoreMode === "choice" ? (
                  <div className="space-y-1.5">
                    <Label>Parametro (Tipo Copertura)</Label>
                    <Select value={parameterCode} onValueChange={setParameterCode}>
                      <SelectTrigger data-testid="quote-parameter"><SelectValue /></SelectTrigger>
                      <SelectContent>{section && Object.entries(section.parameters).filter(([k]) => k === "fatturato" || k === "soci").map(([k, p]) => <SelectItem key={k} value={k}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label>Parametro</Label>
                    <div data-testid="quote-parameter-fixed" className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-sm text-forest">{paramMeta?.label || "—"}</div>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>{paramMeta?.label || "Valore"}</Label>
                  <Input data-testid="quote-parameter-value" type="number" value={parameterValue}
                    onChange={(e) => setParameterValue(e.target.value)} placeholder="0" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Massimale</Label>
                  <Select value={String(massimale)} onValueChange={setMassimale}>
                    <SelectTrigger data-testid="quote-massimale"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>{section?.massimali.map((m) => <SelectItem key={m} value={String(m)}>{eur(m)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {paramMeta?.type === "tesserati" && !(ambito === "non_sanitaria" && settoreMode === "tesserati") && (
                  <div className="space-y-1.5">
                    <Label>Classe di rischio</Label>
                    <Select value={riskClass} onValueChange={setRiskClass}>
                      <SelectTrigger data-testid="quote-riskclass"><SelectValue /></SelectTrigger>
                      <SelectContent>{config.risk_classes.map((c) => <SelectItem key={c} value={c}>Classe {c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          </Card>

          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold text-forest">Questionario assuntivo</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Tipologia ente</Label>
                <Input data-testid="uw-tipologia" value={answers.tipologia_ente} onChange={(e) => setAnswers({ ...answers, tipologia_ente: e.target.value })} placeholder="es. Cooperativa sociale" /></div>
              <div className="space-y-1.5"><Label>Forma giuridica</Label>
                <Input value={answers.forma_giuridica} onChange={(e) => setAnswers({ ...answers, forma_giuridica: e.target.value })} placeholder="es. ODV" /></div>
            </div>
            <div className="mt-4 space-y-2.5">
              {UW_BOOLS.map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2.5 text-sm">
                  <Checkbox data-testid={`uw-${key}`} checked={!!answers[key]} onCheckedChange={(v) => setAnswers({ ...answers, [key]: !!v })} />
                  <span className="text-forest">{label}</span>
                </label>
              ))}
              {answers.rischio_gia_coperto && (
                <label className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm">
                  <Checkbox checked={!!answers.benestare_direzione} onCheckedChange={(v) => setAnswers({ ...answers, benestare_direzione: !!v })} />
                  <span className="text-amber-800">Benestare Direzione ottenuto</span>
                </label>
              )}
            </div>
            <div className="mt-4 border-t border-border/60 pt-4">
              <SinistriDeclaration value={sinistri} onChange={setSinistri}
                periodoAnni={SINISTRI_YEARS.rc_economia_solidale} testidPrefix="rc-sinistri" />
              <div className="mt-4 border-t border-border/60 pt-4">
                <p className="mb-2 text-sm font-semibold text-forest">Valutazione di adeguatezza (IDD)</p>
                <IddDeclaration value={idd} onChange={setIdd} testidPrefix="rc-idd" />
              </div>
            </div>
          </Card>

          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold text-forest">Garanzie opzionali</h2>
            <div className="space-y-3">
              <div className="rounded-lg border border-border/60 p-3">
                <label className="flex items-center gap-3 text-sm font-medium text-forest">
                  <Checkbox data-testid="cov-rco" checked={cov.rco.selected} onCheckedChange={(v) => setCov({ ...cov, rco: { ...cov.rco, selected: !!v } })} />
                  {COVERAGE_LABELS.rco}
                </label>
                {cov.rco.selected && (
                  <label className="mt-2 flex items-center gap-2 pl-7 text-xs text-muted-foreground">
                    <Switch checked={cov.rco.abrogazione} onCheckedChange={(v) => setCov({ ...cov, rco: { ...cov.rco, abrogazione: v } })} />
                    Abrogazione franchigia (+20%, min €50)
                  </label>
                )}
                {cov.rco.selected && (
                  <div className="mt-2 pl-7">
                    <Label className="text-xs text-muted-foreground">Massimale RCO (può differire dalla RCT)</Label>
                    <Select value={String(cov.rco.massimale || massimale)} onValueChange={(v) => setCov({ ...cov, rco: { ...cov.rco, massimale: Number(v) } })}>
                      <SelectTrigger data-testid="cov-rco-massimale" className="mt-1 h-9 w-48"><SelectValue placeholder="Come RCT" /></SelectTrigger>
                      <SelectContent>{section?.massimali.map((m) => <SelectItem key={m} value={String(m)}>{eur(m)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="rounded-lg border border-border/60 p-3">
                <label className={`flex items-center gap-3 text-sm font-medium ${organiEligible ? "text-forest" : "text-muted-foreground"}`}>
                  <Checkbox data-testid="cov-organi" disabled={!organiEligible} checked={cov.organi_sociali.selected} onCheckedChange={(v) => setCov({ ...cov, organi_sociali: { ...cov.organi_sociali, selected: !!v } })} />
                  {COVERAGE_LABELS.organi_sociali}
                </label>
                {!organiEligible && (
                  <p data-testid="cov-organi-blocked" className="mt-1.5 pl-7 text-[11px] text-amber-700">
                    {settorePrincipale ? (covElig.organi_sociali?.message || "Garanzia non ammessa per il soggetto selezionato.") : "Seleziona prima il Settore Principale del cliente."}
                  </p>
                )}
                {organiEligible && cov.organi_sociali.selected && (
                  <div className="mt-2 pl-7">
                    <Select value={String(cov.organi_sociali.massimale)} onValueChange={(v) => setCov({ ...cov, organi_sociali: { ...cov.organi_sociali, massimale: Number(v) } })}>
                      <SelectTrigger className="h-9 w-48"><SelectValue /></SelectTrigger>
                      <SelectContent>{[25000, 50000, 100000, 250000].map((m) => <SelectItem key={m} value={String(m)}>{eur(m)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="rounded-lg border border-border/60 p-3">
                <label className={`flex items-center gap-3 text-sm font-medium ${tutelaEligible ? "text-forest" : "text-muted-foreground"}`}>
                  <Checkbox data-testid="cov-tutela" disabled={!tutelaEligible} checked={cov.tutela_legale.selected} onCheckedChange={(v) => setCov({ ...cov, tutela_legale: { ...cov.tutela_legale, selected: !!v } })} />
                  {COVERAGE_LABELS.tutela_legale}
                </label>
                {!tutelaEligible && (
                  <p data-testid="cov-tutela-blocked" className="mt-1.5 pl-7 text-[11px] text-amber-700">
                    {settorePrincipale ? (covElig.tutela_legale?.message || "Garanzia non ammessa per il soggetto selezionato.") : "Seleziona prima il Settore Principale del cliente."}
                  </p>
                )}
                {tutelaEligible && cov.tutela_legale.selected && (
                  <div className="mt-2 grid grid-cols-3 gap-2 pl-7">
                    <Select value={cov.tutela_legale.option} onValueChange={(v) => setCov({ ...cov, tutela_legale: { ...cov.tutela_legale, option: v } })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tutti_componenti">Tutti i componenti</SelectItem>
                        <SelectItem value="solo_organi_ets">Solo organi ETS</SelectItem>
                        <SelectItem value="solo_volontari">Solo volontari</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={String(cov.tutela_legale.massimale)} onValueChange={(v) => setCov({ ...cov, tutela_legale: { ...cov.tutela_legale, massimale: Number(v) } })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{[15000, 25000].map((m) => <SelectItem key={m} value={String(m)}>{eur(m)}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="h-9" type="number" placeholder="n° soci" value={cov.tutela_legale.soci}
                      onChange={(e) => setCov({ ...cov, tutela_legale: { ...cov.tutela_legale, soci: e.target.value } })} />
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Maggiorazioni</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {config.maggiorazioni.map((m) => (
                  <button key={m.code} data-testid={`magg-${m.code}`} onClick={() => toggleMagg(m.code)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      maggiorazioni.includes(m.code) ? "border-forest bg-forest text-white" : "border-border text-forest/70 hover:bg-sage"}`}>
                    {m.label} +{Math.round(m.pct * 100)}%
                  </button>
                ))}
              </div>
              {config.maggiorazioni.filter((m) => m.note && maggiorazioni.includes(m.code)).map((m) => (
                <p key={m.code} data-testid={`magg-note-${m.code}`} className="mt-2 rounded-md border border-forest/20 bg-sage/40 px-3 py-2 text-[11px] leading-snug text-forest">
                  {m.note}
                </p>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-5 lg:col-span-2">
          <Card className="sticky top-24 rounded-xl border-border/60 p-5">
            <h2 className="mb-3 font-heading text-lg font-semibold text-forest">Leva commerciale</h2>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">{slider < 0 ? "Sconto" : "Aumento"}</span>
              <span data-testid="slider-value" className={`font-heading text-2xl font-semibold ${slider < 0 ? "text-terracotta" : "text-forest"}`}>{slider > 0 ? "+" : ""}{slider}%</span>
            </div>
            <Slider data-testid="commercial-slider" className="mt-3" value={[slider]} min={sliderMin} max={SLIDER_MAX} step={5}
              onValueChange={(v) => setSlider(v[0])} />
            <div className="mt-1 flex justify-between text-[11px] text-muted-foreground"><span>{sliderMin}%</span><span>+300%</span></div>

            <Button data-testid="calculate-button" onClick={calculate} disabled={calculating || !parameterValue || !massimale || (ambito === "sanitaria" && (!settorePrincipale || !tipoPrincipale)) || (ambito === "non_sanitaria" && (!settorePrincipale || ((settoreMode === "tesserati" || settoreMode === "choice") && !tipoPrincipale)))}
              className="mt-5 h-12 w-full gap-2 rounded-xl bg-forest hover:bg-forest-hover">
              {calculating ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Calculator className="h-5 w-5" /> Calcola premio</>}
            </Button>

            {result && (
              <div className="mt-5 space-y-4" data-testid="quote-result">
                {statusBanner && (
                  <div className={`flex gap-2 rounded-lg border p-3 text-sm ${statusBanner.tone}`}>
                    <statusBanner.icon className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <div className="font-medium">{statusBanner.title}</div>
                      {statusBanner.msgs.map((m, i) => <div key={i} className="mt-0.5 text-xs opacity-90">{m}</div>)}
                    </div>
                  </div>
                )}

                {result.premium && (
                  <div className="rounded-lg bg-sage/50 p-4">
                    {Object.entries(result.premium.breakdown).map(([k, b]) => (
                      <div key={k} className="flex justify-between py-1 text-sm">
                        <span className="text-muted-foreground">{COVERAGE_LABELS[k] || k}</span>
                        <span className="font-medium text-forest">{eur(b.gross)}</span>
                      </div>
                    ))}
                    <div className="mt-2 flex items-baseline justify-between border-t border-border pt-2">
                      <span className="font-medium text-forest">Premio annuo lordo</span>
                      <span data-testid="premio-finale" className="font-heading text-2xl font-semibold text-forest">{eur(result.premium.gross_total)}</span>
                    </div>
                    {result.premio_tariffa != null && result.premio_tariffa !== result.premium.gross_total && (
                      <div className="text-right text-xs text-muted-foreground">da tariffa: {eur(result.premio_tariffa)}</div>
                    )}
                    {result.premium.minimum_premium_applied && (
                      <div className="mt-1 text-xs text-amber-700">Applicato premio minimo di sezione.</div>
                    )}
                  </div>
                )}

                {result.garanzie_non_applicabili?.length > 0 && (
                  <div data-testid="garanzie-non-applicabili" className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                    {result.garanzie_non_applicabili.map((g) => (
                      <p key={g.code} className="text-[11px] leading-snug text-amber-800">
                        <span className="font-semibold">«{g.label}» non applicabile a questo soggetto</span> (Norme Tariffarie). {g.message}
                      </p>
                    ))}
                  </div>
                )}

                {result.provvigione && (
                  <div data-testid="commission-box" className="rounded-lg border border-terracotta/30 bg-terracotta/5 p-4">
                    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-terracotta">
                      <Wallet className="h-3.5 w-3.5" /> Provvigione (solo per te)
                    </div>
                    <div className="mt-1 flex items-baseline justify-between">
                      <span className="text-sm text-muted-foreground">{result.provvigione.pct}% dell'imponibile</span>
                      <span className="font-heading text-xl font-semibold text-terracotta">{eur(result.provvigione.importo)}</span>
                    </div>
                  </div>
                )}

                {result.status !== "non_ammissibile" && (
                  <Button data-testid="save-quote-button" onClick={save} disabled={saving || !anagraficaId}
                    className="h-12 w-full gap-2 rounded-xl bg-terracotta hover:bg-terracotta-hover">
                    {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-4 w-4" /> Salva preventivo</>}
                  </Button>
                )}
                <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  <Lock className="h-3 w-3" /> Le provvigioni non compaiono mai nella vista cliente.
                </p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
